import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip, Legend as ReLegend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area
} from 'recharts';
import './Dashboard.css';
import './SupervisorDashboard.css';

const SupervisorDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState({});
  const [filters, setFilters] = useState({
    categoria: '',
    estado: '',
    periodo: 'all',
    startDate: '',
    endDate: '',
    departamento: 'Todas',
    estadoFuncionario: 'Activo',
    historialUsuario: ''
  });

  const [activeView, setActiveView] = useState('inventory'); // 'inventory' o 'personnel'
  const [funcData, setFuncData] = useState(null);

  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const apiBase = isLocal ? `http://${window.location.hostname}:3001` : `https://${window.location.hostname}`;

  const handleExportCSV = () => {
    if (!data?.equipos || data.equipos.length === 0) return alert("No hay datos para exportar");

    const BOM = "\uFEFF";
    const headers = ["Codigo", "Modelo", "Estado", "Categoria", "Custodio", "Fecha Creado"];
    const rows = data.equipos.map(e => [
      `"${e.codigo || 'S/D'}"`,
      `"${e.modelo || 'S/D'}"`,
      `"${e.estado || 'S/D'}"`,
      `"${e.categoria || 'S/D'}"`,
      `"${e.custodio_nombre || 'Sin asignar'}"`,
      `"${e.creado_en ? e.creado_en.split(/[\sT]/)[0] : 'S/D'}"`
    ]);

    const csvContent = BOM + [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Reporte_Inventario_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.categoria) params.append('categoria', filters.categoria);
      if (filters.estado) params.append('estado', filters.estado);
      if (filters.historialUsuario) params.append('usuario', filters.historialUsuario);

      let start, end;
      if (filters.periodo === 'all') {
        start = null;
        end = null;
      } else if (filters.periodo === 'custom') {
        start = filters.startDate;
        end = filters.endDate;
      } else {
        const endDateObj = new Date();
        const startDateObj = new Date();
        startDateObj.setDate(endDateObj.getDate() - parseInt(filters.periodo));
        start = startDateObj.toISOString().split('T')[0];
        end = endDateObj.toISOString().split('T')[0];
      }

      if (start) params.append('startDate', start);
      if (end) params.append('endDate', end);

      const resp = await fetch(`${apiBase}/api/stats/dashboard?${params.toString()}`);
      if (resp.ok) {
        const result = await resp.json();
        setData(result);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiBase, filters]);

  const fetchPersonnelData = useCallback(async () => {
    try {
      const { startDate, endDate, categoria, estado, departamento } = filters;
      const params = new URLSearchParams({
        startDate: startDate || '',
        endDate: endDate || '',
        categoria: categoria || '',
        estado: estado || '',
        departamento: departamento || ''
      });
      const resp = await fetch(`${apiBase}/api/stats/advanced-funcionarios?${params}`);
      if (resp.ok) {
        setFuncData(await resp.json());
      }
    } catch (err) {
      console.error(err);
    }
  }, [apiBase, filters]);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (!savedUser) return navigate('/');
    const u = JSON.parse(savedUser);
    if (!['supervisor', 'admin', 'superadmin'].includes(u.rol)) return navigate('/dashboard');
    setUser(u);
  }, [navigate]);

  useEffect(() => {
    if (user.rol) {
      fetchDashboardData();
    }
  }, [filters.periodo, filters.categoria, filters.estado, filters.startDate, filters.endDate, filters.historialUsuario, user.rol, fetchDashboardData]);

  useEffect(() => {
    if (activeView === 'personnel' && user.rol) {
      fetchPersonnelData();
    }
  }, [activeView, fetchPersonnelData, user.rol, filters.categoria, filters.estado]);

  const COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#f43f5e', '#64748b'];

  const statusPieData = useMemo(() => {
    if (!data?.statusDist) return [];
    return Object.entries(data.statusDist).map(([name, value]) => ({ name, value }));
  }, [data]);

  const categoryBarData = useMemo(() => {
    if (!data?.categoryDist) return [];
    return Object.entries(data.categoryDist)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  const trendData = useMemo(() => data?.trends || [], [data]);

  if (isLoading) {
    return (
      <div className="dashboard-layout shimmer-bg">
        <main className="main-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
          <div className="shimmer-loader-premium">
            <div className="shimmer-circle"></div>
            <div className="shimmer-text">
              <div className="shimmer-line long"></div>
              <div className="shimmer-line short"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const { history, alerts } = data || { history: [], alerts: [] };

  return (
    <div className="dashboard-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h2>Gestor de Equipos</h2>
        </div>
        <nav className="sidebar-nav">
          <Link to="/dashboard" className="nav-item">Equipos</Link>
          <Link to="/funcionarios" className="nav-item">Funcionarios</Link>
          <Link to="/supervisor" className="nav-item active">Panel de Análisis</Link>
          {['admin', 'superadmin'].includes(user.rol) && <Link to="/usuarios" className="nav-item">Usuarios</Link>}
        </nav>
        <div className="sidebar-footer">
          <button className="logout-button" onClick={() => { localStorage.removeItem('user'); navigate('/'); }}>Cerrar Sesión</button>
        </div>
      </aside>

      <main className="main-content">
        <header className="content-header">
          <div className="header-title">
            <span className="p-badge">Inteligencia de Datos</span>
            <h1>Panel de Control de Activos</h1>
            <p>Métricas consolidadas para la gestión estratégica municipal.</p>
          </div>
          <div className="header-actions">
            <div className="view-switcher">
              <button
                className={activeView === 'inventory' ? 'active' : ''}
                onClick={() => setActiveView('inventory')}
              >
                Inventario
              </button>
              <button
                className={activeView === 'personnel' ? 'active' : ''}
                onClick={() => setActiveView('personnel')}
              >
                Personal
              </button>
            </div>
          </div>
        </header>

        <section className="premium-filter-container animate-fade-in">
          <div className="filter-card">
            <div className="filter-group-premium">
              <label>Periodo de Análisis</label>
              <select className="premium-select" value={filters.periodo} onChange={e => setFilters({ ...filters, periodo: e.target.value })}>
                <option value="all">Todo el tiempo</option>
                <option value="7">Últimos 7 días</option>
                <option value="30">Últimos 30 días</option>
                <option value="90">Últimos 90 días</option>
                <option value="custom">Rango Personalizado</option>
              </select>
            </div>

            {filters.periodo === 'custom' && (
              <div className="filter-group-premium">
                <label>Desde / Hasta</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="date" className="premium-date" value={filters.startDate} onChange={e => setFilters({ ...filters, startDate: e.target.value })} />
                  <input type="date" className="premium-date" value={filters.endDate} onChange={e => setFilters({ ...filters, endDate: e.target.value })} />
                </div>
              </div>
            )}

            <div className="filter-group-premium">
              <label>Categoría</label>
              <select className="premium-select" value={filters.categoria} onChange={e => setFilters({ ...filters, categoria: e.target.value })}>
                <option value="">Todas las categorías</option>
                <option value="PC">PC</option>
                <option value="Impresora">Impresora</option>
                <option value="Notebook">Notebook</option>
                <option value="Hardware">Hardware</option>
              </select>
            </div>

            <div className="filter-group-premium">
              <label>Estado del Activo</label>
              <select className="premium-select" value={filters.estado} onChange={e => setFilters({ ...filters, estado: e.target.value })}>
                <option value="">Todos los estados</option>
                <option value="Operativo">Operativo</option>
                <option value="Mantenimiento">Mantenimiento</option>
                <option value="Baja">Baja</option>
              </select>
            </div>

            <div className="premium-button-group" style={{ marginLeft: 'auto' }}>
              <button className="premium-btn-base btn-glass-alt" onClick={() => {
                setFilters({ categoria: '', estado: '', periodo: 'all', startDate: '', endDate: '', historialUsuario: '' });
                setTimeout(() => fetchDashboardData(), 0);
              }}>
                Limpiar Filtros
              </button>
              <button className="premium-btn-base btn-primary-glow" onClick={handleExportCSV}>
                Exportar Reporte
              </button>
            </div>
          </div>
        </section>

        {activeView === 'inventory' ? (
          <div className="inventory-analytics-premium animate-fade-in">
            {!data ? (
              <div className="p-glass-card" style={{ padding: '60px', textAlign: 'center', gridColumn: 'span 4' }}>
                <div style={{ fontSize: '40px', marginBottom: '20px' }}>⚠️</div>
                <h3>Error de Sincronización</h3>
                <p style={{ color: '#64748b' }}>No se pudo conectar con el servidor de inteligencia. Por favor, refresca la página.</p>
              </div>
            ) : data.total === 0 ? (
              <div className="p-glass-card" style={{ padding: '60px', textAlign: 'center', gridColumn: 'span 4' }}>
                <div style={{ fontSize: '40px', marginBottom: '20px' }}>📦</div>
                <h3>El Inventario está Vacío</h3>
                <p style={{ color: '#64748b' }}>No hay equipos registrados para analizar. Registra activos en el <Link to="/dashboard" style={{ color: '#6366f1', fontWeight: 'bold' }}>Dashboard de Equipos</Link> para ver métricas.</p>
              </div>
            ) : (
              <>
                <div className="personnel-stats-row">
                  <div className="p-glass-card indigo">
                    <div className="icon-container"></div>
                    <span className="p-label">Total Activos</span>
                    <span className="p-value">{data?.total || 0}</span>
                    <span className="p-subtext">Inventariados en sistema</span>
                  </div>
                  <div className="p-glass-card blue">
                    <div className="icon-container"></div>
                    <span className="p-label">Operativos</span>
                    <span className="p-value">{data?.active || 0}</span>
                    <span className="p-subtext">Activos en buen estado</span>
                  </div>
                  <div className="p-glass-card amber">
                    <div className="icon-container"></div>
                    <span className="p-label">Mantenimiento</span>
                    <span className="p-value">{data?.maintenance || 0}</span>
                    <span className="p-subtext">En revisión técnica</span>
                  </div>
                  <div className="p-glass-card red">
                    <div className="icon-container"></div>
                    <span className="p-label">Fuera de Servicio</span>
                    <span className="p-value">{data?.retired || 0}</span>
                    <span className="p-subtext">Dados de baja oficial</span>
                  </div>
                </div>

                <div className="analytics-layout-grid-premium">
                  <div className="chart-panel-premium">
                    <div className="panel-header">
                      <div className="indicator-dot blue"></div>
                      <h3>Distribución por Estado</h3>
                    </div>
                    <div className="chart-container" style={{ height: '300px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusPieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            dataKey="value"
                            paddingAngle={5}
                          >
                            {statusPieData.map((entry, index) => {
                              let color = '#6366f1'; // Default
                              if (entry.name === 'Operativo') color = '#0ea5e9';
                              if (entry.name === 'Mantenimiento') color = '#f59e0b';
                              if (entry.name === 'Baja') color = '#f43f5e';
                              return <Cell key={`cell-${index}`} fill={color} />;
                            })}
                          </Pie>
                          <ReTooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '12px', fontWeight: '600' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Nueva Leyenda de Estados */}
                    <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#0ea5e9' }}></div>
                        Operativo
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f59e0b' }}></div>
                        Mantenimiento
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f43f5e' }}></div>
                        Baja
                      </div>
                    </div>
                  </div>

                  <div className="chart-panel-premium">
                    <div className="panel-header">
                      <div className="indicator-dot amber"></div>
                      <h3>Alertas y Utilización</h3>
                    </div>
                    <div className="p-alerts-feed">
                      {alerts.length > 0 ? alerts.map((a, i) => (
                        <div key={i} className={`p-alert-card ${a.type}`}>
                          <div className="alert-content">
                            <span className="alert-category">{a.type === 'danger' ? 'CRÍTICO' : a.type === 'warning' ? 'ADVERTENCIA' : 'INFO'}</span>
                            <p className="alert-text">{a.text}</p>
                          </div>
                        </div>
                      )) : (
                        <div className="p-empty-state">No hay alertas críticas en este periodo de análisis.</div>
                      )}

                      <div className="usage-meter-premium">
                        <div className="usage-header">
                          <span className="usage-label">Utilización del Inventario</span>
                          <span className="usage-value">{Math.round(data?.personnel?.utilization_pct || 0)}%</span>
                        </div>
                        <div className="premium-meter-rail">
                          <div
                            className="premium-meter-track"
                            style={{ width: `${data?.personnel?.utilization_pct || 0}%` }}
                          >
                            <div className="meter-glow"></div>
                          </div>
                        </div>
                        <p className="usage-hint">Ratio de equipos asignados vs total operativo</p>
                      </div>
                    </div>
                  </div>

                  <div className="chart-panel-premium" style={{ gridColumn: 'span 2' }}>
                    <div className="panel-header">
                      <div className="indicator-dot indigo"></div>
                      <h3>Análisis de Actividad y Tendencias</h3>
                    </div>

                    <div className="charts-insight-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '32px', marginBottom: '24px' }}>
                      <div className="chart-box">
                        <p style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '16px', textTransform: 'uppercase' }}>Distribución de Equipos</p>
                        <div style={{ height: '240px' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={categoryBarData} layout="vertical" margin={{ right: 30 }}>
                              <XAxis type="number" hide />
                              <YAxis dataKey="name" type="category" width={80} axisLine={false} tickLine={false} style={{ fontSize: '12px', fontWeight: '700', fill: '#1e293b' }} />
                              <ReTooltip cursor={{ fill: 'rgba(99, 102, 241, 0.04)' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '11px' }} />
                              <Bar dataKey="value" fill="url(#colorIndigo)" radius={[0, 4, 4, 0]}>
                                <labelList dataKey="value" position="right" style={{ fill: '#64748b', fontSize: '12px', fontWeight: '800' }} />
                              </Bar>
                              <defs>
                                <linearGradient id="colorIndigo" x1="0" y1="0" x2="1" y2="0">
                                  <stop offset="0%" stopColor="#6366f1" />
                                  <stop offset="100%" stopColor="#818cf8" />
                                </linearGradient>
                              </defs>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="chart-box">
                        <p style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '16px', textTransform: 'uppercase' }}>Ritmo de Movimientos Diarios</p>
                        <div style={{ height: '240px' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trendData}>
                              <defs>
                                <linearGradient id="colorTrend" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <XAxis dataKey="dia" axisLine={false} tickLine={false} style={{ fontSize: '10px', fill: '#94a3b8' }} tickFormatter={(val) => val.split('-').slice(1).join('/')} />
                              <YAxis axisLine={false} tickLine={false} style={{ fontSize: '10px', fill: '#94a3b8' }} />
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                              <ReTooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '11px' }} />
                              <Area type="monotone" dataKey="asignaciones" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorTrend)" name="Movimientos" />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>

                    <div className="behavior-insight-panel" style={{ background: '#f8fafc', padding: '20px', borderRadius: '16px', border: '1px solid #edf2f7', display: 'flex', gap: '20px', alignItems: 'center' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#6366f1', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>i</div>
                      <div>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '800', color: '#1e293b' }}>Resumen de Tendencias</h4>
                        <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: '1.5' }}>
                          {trendData.length > 0 ? (
                            <>Se detectó un pico de actividad el día <strong>{trendData[trendData.length - 2]?.dia || 'reciente'}</strong>. La categoría predominante sigue siendo <strong>{categoryBarData[0]?.name || 'N/A'}</strong> con {categoryBarData[0]?.value || 0} unidades.</>
                          ) : 'No hay datos suficientes para generar un análisis de tendencia en este rango.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="chart-panel-premium" style={{ gridColumn: 'span 2' }}>
                    <div className="panel-header" style={{ justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div className="indicator-dot blue"></div>
                        <h3>Trazabilidad de Movimientos</h3>
                      </div>
                      <div className="history-filter-inline">
                        <select
                          className="premium-select-compact"
                          value={filters.historialUsuario}
                          onChange={e => setFilters({ ...filters, historialUsuario: e.target.value })}
                        >
                          <option value="">Filtrar por Admin / Operador</option>
                          {data?.uniqueUsers?.map((u, i) => (
                            <option key={i} value={u.usuario_uid}>{u.usuario_uid}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="activity-feed-compact" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {history.length > 0 ? history.map((h, i) => (
                        <div className="activity-item-premium" key={i}>
                          <div className="activity-main">
                            <div className="activity-indicator"></div>
                            <div className="activity-content-box">
                              <div className="activity-meta-top">
                                <span className="p-badge-outline">{h.codigo}</span>
                                <span className="activity-time">{h.fecha}</span>
                              </div>
                              <p className="activity-text">{h.detalle}</p>
                            </div>
                          </div>
                          <div className="activity-user-badge">
                            <div className="u-avatar-mini">{h.usuario_uid?.charAt(0)}</div>
                            <span className="u-name-mini">{h.usuario_uid}</span>
                          </div>
                        </div>
                      )) : (
                        <div className="p-empty-state">No hay movimientos que coincidan con los filtros.</div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="personnel-analytics-premium animate-fade-in">
            <div className="panel-executive-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#1e293b' }}>Análisis de Personal</h2>
              <div className="local-filter-group" style={{ display: 'flex', gap: '12px' }}>
                <select
                  className="premium-select-compact"
                  value={filters.departamento}
                  onChange={e => setFilters({ ...filters, departamento: e.target.value })}
                >
                  <option value="Todas">Todos los Departamentos</option>
                  {funcData?.distributions?.equipsByDept?.map(d => (
                    <option key={d.name} value={d.name}>{d.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {!funcData ? (
              <div className="p-glass-card" style={{ padding: '60px', textAlign: 'center' }}>
                <div style={{ fontSize: '40px', marginBottom: '20px' }}>⚠️</div>
                <h3>Error de Sincronización</h3>
                <p style={{ color: '#64748b' }}>No se pudo conectar con el servidor de personal.</p>
              </div>
            ) : funcData.metrics?.totalFuncionarios === 0 ? (
              <div className="p-glass-card" style={{ padding: '60px', textAlign: 'center' }}>
                <div style={{ fontSize: '40px', marginBottom: '20px' }}>👥</div>
                <h3>Sin Datos de Personal</h3>
                <p style={{ color: '#64748b' }}>No se encontraron funcionarios registrados en el sistema.</p>
              </div>
            ) : (
              <>
                <div className="personnel-stats-row">
                  <div className="p-glass-card blue">
                    <div className="icon-container"></div>
                    <span className="p-label">Personal Total</span>
                    <span className="p-value">{funcData?.metrics?.totalFuncionarios || 0}</span>
                    <span className="p-subtext">Registrados en la entidad</span>
                  </div>
                  <div className="p-glass-card emerald">
                    <div className="icon-container"></div>
                    <span className="p-label">Responsables</span>
                    <span className="p-value">{funcData?.metrics?.conEquipos || 0}</span>
                    <span className="p-subtext">Con activos a su cargo</span>
                  </div>
                  <div className="p-glass-card amber">
                    <div className="icon-container"></div>
                    <span className="p-label">Sin Asignación</span>
                    <span className="p-value">{funcData?.metrics?.sinEquipos || 0}</span>
                    <span className="p-subtext">Funcionarios sin inventario</span>
                  </div>
                  <div className="p-glass-card indigo">
                    <div className="icon-container"></div>
                    <span className="p-label">Carga Promedio</span>
                    <span className="p-value">
                      {funcData?.metrics?.totalFuncionarios > 0
                        ? (funcData?.metrics?.totalEquips / funcData?.metrics?.totalFuncionarios).toFixed(1)
                        : '0.0'}
                    </span>
                    <span className="p-subtext">Equipos por funcionario</span>
                  </div>
                </div>

                <div className="analytics-layout-grid-premium">
                  <div className="chart-panel-premium">
                    <div className="panel-header">
                      <div className="indicator-dot blue"></div>
                      <h3>Equipos por Departamento</h3>
                    </div>
                    <div className="chart-container" style={{ height: '320px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={funcData?.distributions?.equipsByDept || []}
                            cx="50%" cy="50%"
                            innerRadius={70}
                            outerRadius={100}
                            paddingAngle={8}
                            dataKey="value"
                          >
                            {(funcData?.distributions?.equipsByDept || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="rgba(255,255,255,0.2)" strokeWidth={2} />
                            ))}
                          </Pie>
                          <ReTooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '11px', fontWeight: '800' }} />
                          <ReLegend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '11px', fontWeight: '800', color: '#64748b' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="chart-panel-premium">
                    <div className="panel-header">
                      <div className="indicator-dot red"></div>
                      <h3>Alertas de Distribución</h3>
                    </div>
                    <div className="p-alerts-feed">
                      {funcData?.alerts?.length > 0 ? funcData.alerts.map((a, i) => (
                        <div key={i} className={`p-alert-card ${a.type}`}>
                          <div className="alert-content">
                            <span className="alert-category">{a.type === 'danger' ? 'CRÍTICO' : a.type === 'warning' ? 'ADVERTENCIA' : 'INFO'}</span>
                            <p className="alert-text">{a.text}</p>
                          </div>
                        </div>
                      )) : (
                        <div className="p-empty-state">Distribución de activos equilibrada y sin novedades.</div>
                      )}
                    </div>
                  </div>

                  <div className="chart-panel-premium" style={{ gridColumn: 'span 2' }}>
                    <div className="panel-header">
                      <div className="indicator-dot blue"></div>
                      <h3>Carga de Responsabilidad por Funcionario</h3>
                    </div>
                    <div className="p-ranking-table">
                      <table className="func-table-premium">
                        <thead>
                          <tr>
                            <th>Funcionario</th>
                            <th>Departamento</th>
                            <th>Activos</th>
                            <th style={{ width: '150px' }}>Nivel de Carga</th>
                          </tr>
                        </thead>
                        <tbody>
                          {funcData?.individuals?.slice(0, 8).map(f => (
                            <tr key={f.id} className="row-hover">
                              <td>
                                <div className="f-identity">
                                  <div className="f-avatar" style={{ background: COLORS[f.id?.length % COLORS.length] }}>{f.nombre?.charAt(0)}</div>
                                  <div className="f-meta">
                                    <span className="f-name">{f.nombre}</span>
                                    <span className="f-tag">{f.cargo}</span>
                                  </div>
                                </div>
                              </td>
                              <td><span className="p-badge-outline" style={{ opacity: 0.7 }}>{f.departamento || 'General'}</span></td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span className="f-count" style={{ fontSize: '16px' }}>{f.total_equipos}</span>
                                  <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '700' }}>equipos</span>
                                </div>
                              </td>
                              <td>
                                <div className="meter-rail" style={{ height: '8px', background: '#f1f5f9', borderRadius: '10px', overflow: 'hidden', width: '100px' }}>
                                  <div className="meter-track" style={{
                                    width: `${Math.min((f.total_equipos / 6) * 100, 100)}%`,
                                    height: '100%',
                                    background: f.total_equipos > 5 ? 'var(--p-rose)' : f.total_equipos > 3 ? 'var(--p-amber)' : 'var(--p-indigo)',
                                    borderRadius: '10px',
                                    boxShadow: f.total_equipos > 3 ? '0 0 10px rgba(0,0,0,0.05)' : 'none'
                                  }}></div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default SupervisorDashboard;
